import { ImageAnalysisResult, ImageAnalysisTokenUsage } from '@aloft/toolkit-shared';
import { Block, Flex, Typography } from '@aloft/toolkit-sui';

type Timing = NonNullable<NonNullable<ImageAnalysisResult['_debug']>['timing']>;

const tokenColor = (count: number, type: 'prompt' | 'output' | 'total'): string => {
  // Thresholds based on typical image analysis requests (10–30 images):
  // Prompt: images dominate, ~1k tokens/image → warn at 40k, red at 80k
  // Output: structured JSON + prose → warn at 1.5k, red at 3k
  // Total: warn at 50k, red at 90k
  // eslint-disable-next-line no-nested-ternary
  const [warn, danger] =
    type === 'prompt' ? [40_000, 80_000] : type === 'output' ? [1_500, 3_000] : [50_000, 90_000];
  if (count >= danger) return '#c0392b';
  if (count >= warn) return '#e67e22';
  return '#27ae60';
};

const StatChip = ({
  label,
  value,
  valueColor,
}: {
  label: string;
  value: string;
  valueColor?: string;
}) => (
  <Block padding="8px 12px" borderRadius="4px" backgroundColor="surface2" minWidth="100px">
    <Typography variant="body2" color="textSecondary">
      {label}
    </Typography>
    <div style={valueColor != null ? { color: valueColor } : undefined}>
      <Typography variant="h4">{value}</Typography>
    </div>
  </Block>
);

export const TimingBar = ({
  timing,
  tokenUsage,
}: {
  timing: Timing;
  tokenUsage?: ImageAnalysisTokenUsage;
}) => (
  <Flex gap="16px" flexWrap="wrap">
    <StatChip label="Upload" value={`${(timing.uploadMs / 1000).toFixed(2)}s`} />
    <StatChip label="Generate" value={`${(timing.generateMs / 1000).toFixed(2)}s`} />
    <StatChip label="Parse" value={`${(timing.parseMs / 1000).toFixed(2)}s`} />
    <StatChip label="Total" value={`${(timing.totalMs / 1000).toFixed(2)}s`} />
    {tokenUsage != null && (
      <>
        <StatChip
          label="Prompt tokens"
          value={tokenUsage.promptTokenCount.toLocaleString()}
          valueColor={tokenColor(tokenUsage.promptTokenCount, 'prompt')}
        />
        <StatChip
          label="Output tokens"
          value={tokenUsage.candidatesTokenCount.toLocaleString()}
          valueColor={tokenColor(tokenUsage.candidatesTokenCount, 'output')}
        />
        {tokenUsage.thoughtsTokenCount != null && (
          <StatChip
            label="Thoughts tokens"
            value={tokenUsage.thoughtsTokenCount.toLocaleString()}
          />
        )}
        <StatChip
          label="Total tokens"
          value={tokenUsage.totalTokenCount.toLocaleString()}
          valueColor={tokenColor(tokenUsage.totalTokenCount, 'total')}
        />
      </>
    )}
  </Flex>
);
